import { useState } from 'react';

import { useOfflineQueueItems } from '../hooks/useOfflineQueue';
import { offlineQueue, type OfflineQueue, type QueueItem } from '../lib/offlineQueue';

const describe = (item: QueueItem): string => {
  const { action } = item;
  const excerpt = (text: string) => (text.length > 60 ? `${text.slice(0, 60)}…` : text);
  switch (action.kind) {
    case 'taskComment':
      return `Komentár: ${excerpt(action.body)}`;
    case 'chatMessage':
      return `Správa: ${excerpt(action.body)}`;
    case 'note':
      return `Poznámka: ${excerpt(action.payload.title)}`;
    case 'todo':
      return `Úloha: ${excerpt(action.payload.title)}`;
    case 'taskStatus':
      return 'Presun tiketu';
  }
};

/**
 * Čo čaká na odoslanie a čo server odmietol.
 *
 * Odmietnutá zmena tu ostáva, kým sa človek nerozhodne — zmiznúť potichu by
 * znamenalo, že si myslí, že komentár napísal, a nikto ho nikdy nevidel.
 */
export const OfflineQueueBar = ({ queue = offlineQueue() }: { queue?: OfflineQueue }) => {
  const items = useOfflineQueueItems(queue);
  const [expanded, setExpanded] = useState(false);
  const pending = items.filter((item) => item.failed === null);
  const failed = items.filter((item) => item.failed !== null);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-b border-black/[0.06] bg-sky-500/10 px-4 py-2 text-[13px] text-sky-900 dark:border-white/10 dark:bg-sky-400/10 dark:text-sky-200"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-3 gap-y-1">
        {pending.length > 0 ? (
          <span className="font-medium">Čaká na odoslanie ({pending.length})</span>
        ) : null}
        {failed.length > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            className="font-medium text-rose-700 underline-offset-2 hover:underline dark:text-rose-300"
          >
            Neodoslané ({failed.length})
          </button>
        ) : null}
      </div>
      {failed.length > 0 && expanded ? (
        <ul className="mx-auto mt-2 max-w-6xl space-y-1.5">
          {failed.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 px-2.5 py-1.5 text-slate-700 dark:bg-black/30 dark:text-slate-200"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">{describe(item)}</span>
                <span className="block text-[12px] text-rose-700 dark:text-rose-300">
                  {item.failed?.message}
                </span>
              </span>
              <button
                type="button"
                onClick={() => void queue.retry(item.id)}
                className="rounded-md px-2 py-1 font-medium text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
              >
                Skúsiť znova
              </button>
              <button
                type="button"
                onClick={() => queue.discard(item.id)}
                className="rounded-md px-2 py-1 font-medium text-slate-500 hover:bg-black/[0.05] dark:hover:bg-white/10"
              >
                Zahodiť
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
